'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'

/**
 * Parses a raw URL param string into a typed value, returning undefined when
 * the param is missing, malformed, or doesn't match a known vocabulary.
 */
export type QueryParser<T> = (raw: string) => T | undefined

export type ToolQuerySchema = Record<string, QueryParser<unknown>>

export type ParsedQuery<S extends ToolQuerySchema> = {
  [K in keyof S]: ReturnType<S[K]>
}

/**
 * Required keys are non-nullable; optional keys keep their original
 * `T | undefined` shape so callers can still read them.
 */
type AutoSubmitValues<S extends ToolQuerySchema, R extends keyof S> = {
  [K in keyof S]: K extends R
    ? NonNullable<ReturnType<S[K]>>
    : ReturnType<S[K]>
}

export interface UseToolFromQueryOptions<
  S extends ToolQuerySchema,
  R extends keyof S = never
> {
  parsers: S
  /**
   * Keys that must all parse successfully for `onAutoSubmit` to fire.
   * If empty/omitted, `onAutoSubmit` never fires automatically.
   */
  required?: ReadonlyArray<R>
  /**
   * Called once on mount (and whenever the URL params change) when every
   * required key has a parsed value. Receives the typed values.
   */
  onAutoSubmit?: (values: AutoSubmitValues<S, R>) => void
}

/**
 * Reads URL search params through a typed parser map. Use for prefilling
 * tool forms from shareable links and for auto-submitting when all required
 * params are present.
 *
 * Example:
 *   const { parsed } = useToolFromQuery({
 *     parsers: { grass: parseGrassType, zip: parseZipCode },
 *     required: ['grass', 'zip'] as const,
 *     onAutoSubmit: ({ grass, zip }) => handleSubmit({ grassType: grass, zipCode: zip }),
 *   })
 */
export function useToolFromQuery<
  S extends ToolQuerySchema,
  R extends keyof S = never
>(options: UseToolFromQueryOptions<S, R>): { parsed: ParsedQuery<S> } {
  const searchParams = useSearchParams()
  const { parsers, required, onAutoSubmit } = options
  const lastAutoSubmitKeyRef = useRef<string | null>(null)

  const parsed = useMemo(() => {
    const result = {} as ParsedQuery<S>
    for (const key in parsers) {
      const raw = searchParams.get(key)
      result[key] = (raw ? parsers[key](raw) : undefined) as ParsedQuery<S>[typeof key]
    }
    return result
  }, [searchParams, parsers])

  useEffect(() => {
    if (!onAutoSubmit) return
    if (!required || required.length === 0) return
    const allPresent = required.every(k => parsed[k] !== undefined)
    if (!allPresent) return
    const autoSubmitKey = required
      .map(k => `${String(k)}:${String(parsed[k])}`)
      .join('|')
    if (lastAutoSubmitKeyRef.current === autoSubmitKey) return
    lastAutoSubmitKeyRef.current = autoSubmitKey
    onAutoSubmit(parsed as unknown as AutoSubmitValues<S, R>)
    // Auto-submit only depends on the parsed values, not the callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed])

  return { parsed }
}

// ---------- Common parsers ----------
// Reusable parsers for the URL-param vocabulary the tools share. Tools with
// tool-specific enums (e.g., watering's irrigation system) should keep their
// own parser inline alongside the rest of their logic.

export const parseZipCode: QueryParser<string> = (raw) => {
  return /^\d{5}$/.test(raw) ? raw : undefined
}

export const parseLawnSize: QueryParser<number> = (raw) => {
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 && n <= 1_000_000 ? n : undefined
}

/**
 * Maps URL-friendly grass slugs (case-insensitive, dashes or spaces) to the
 * canonical GRASS_CHARACTERISTICS key. Returns undefined if no match.
 */
export const parseGrassType: QueryParser<string> = (raw) => {
  // Treat dashes, underscores AND periods as separators so the canonical guide
  // slugs round-trip — notably "st-augustinegrass" must match the key
  // "St. Augustinegrass" (the period is the only thing that differs). Normalize
  // BOTH the input and the key the same way.
  const norm = (s: string) => s.toLowerCase().replace(/[-_.]+/g, ' ').replace(/\s+/g, ' ').trim()
  const normalized = norm(raw)
  return Object.keys(GRASS_CHARACTERISTICS).find(name => norm(name) === normalized)
}

export const parseSeason: QueryParser<'spring' | 'summer' | 'fall' | 'winter'> = (raw) => {
  const v = raw.toLowerCase()
  if (v === 'spring' || v === 'summer' || v === 'fall' || v === 'winter') return v
  return undefined
}
